import { CheckCircle, Circle, QrCode, MapPin, Clock } from 'lucide-react';
import type { PatrolRound, PatrolCheckpoint } from '@yawi/types';
import { useI18n } from '@/shared/i18n';

interface PatrolProgressProps {
  round: PatrolRound;
  checkpoints: PatrolCheckpoint[];
  onScanCheckpoint?: (checkpointId: string) => void;
}

export const PatrolProgress = ({ round, checkpoints, onScanCheckpoint }: PatrolProgressProps) => {
  const { t } = useI18n();

  // Get scanned checkpoint IDs
  const scans = round.scans || [];
  const scannedIds = new Set(scans.map(s => s.checkpointId));

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">{t('patrol.progress')}</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">
            {round.scannedCheckpoints}/{round.totalCheckpoints}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            round.completionPercentage === 100
              ? 'bg-green-100 text-green-700'
              : 'bg-blue-100 text-blue-700'
          }`}>
            {round.completionPercentage.toFixed(0)}%
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
        <div
          className={`h-2 rounded-full transition-all ${
            round.completionPercentage === 100
              ? 'bg-green-500'
              : 'bg-primary-500'
          }`}
          style={{ width: `${round.completionPercentage}%` }}
        />
      </div>

      {/* Checkpoints list */}
      <div className="space-y-2">
        {checkpoints.map((checkpoint, index) => {
          const isScanned = scannedIds.has(checkpoint.id);
          const scan = scans.find(s => s.checkpointId === checkpoint.id);

          return (
            <div
              key={checkpoint.id}
              className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                isScanned
                  ? 'bg-green-50 border border-green-200'
                  : 'bg-slate-50 border border-slate-200'
              }`}
            >
              {/* Status icon */}
              <div className={`flex-shrink-0 ${isScanned ? 'text-green-600' : 'text-slate-400'}`}>
                {isScanned ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <Circle className="w-5 h-5" />
                )}
              </div>

              {/* Checkpoint info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-slate-500">#{index + 1}</span>
                  <span className="font-medium text-slate-800 truncate">{checkpoint.name}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">{checkpoint.location}</span>
                </div>
                {scan && (
                  <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatTime(scan.scannedAt)}</span>
                  </div>
                )}
              </div>

              {/* Scan button */}
              {!isScanned && round.status === 'IN_PROGRESS' && onScanCheckpoint && (
                <button
                  onClick={() => onScanCheckpoint(checkpoint.id)}
                  className="flex-shrink-0 p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <QrCode className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
